import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Input, Modal, Row } from 'antd';
import { rowGutter } from '../../../../util/forms';
import { Project } from '../../../../models/Project';
import { useForm } from 'antd/lib/form/Form';
import { apiFetchConfigurations } from '../../../settings/api';
import { ConfigurationKeys } from '../../../../models/Types';
import {
  createTemporaryId,
  defaultProjectNrPlaceholder,
} from '../../../../util';
import { useDispatch } from 'react-redux';
import { createSubproject, setActiveProjectTabView } from '../../actions';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../../theme/types';
import { useProjectDraftValidate } from '../../hooks/useProjectDraftValidate';

interface CreateSubprojectModalProps {
  visible: boolean;
  project: Project;
  onClose: VoidFunction;
}

export const CreateSubprojectModal: React.FC<CreateSubprojectModalProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { visible, project, onClose } = props;
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  const [form] = useForm();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const initialValues = useMemo(
    () => ({
      name: '',
      subprojectNumber: `${project.number}-`,
      shortName: '',
    }),
    [project]
  );

  const [projectNrPlaceholder, setProjectNumberPlaceholder] =
    useState<string>(null);

  const [disableActionButton, setDisableActionButton] = useState<boolean>(true);
  var {
    validationResult,
    validateProjectCreationData,
    projectToCreateIsValid,
  } = useProjectDraftValidate(form);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOk = () => {
    form.validateFields().then((values) => {
      dispatch(createSubproject(values, project, createTemporaryId()));
      dispatch(setActiveProjectTabView('myProjects'));
      handleClose();
    });
  };

  const handleClose = () => {
    onClose();
    form.resetFields();
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const loadConfigs = async () => {
      const { data } = await apiFetchConfigurations();
      if (data) {
        const config = data.find(
          (configuration) =>
            configuration.key === ConfigurationKeys.PLACEHOLDER_PROJECTNR
        );
        setProjectNumberPlaceholder(config?.value ?? null);
      }
    };
    loadConfigs();
  }, []);

  useEffect(() => {
    if (validationResult) {
      if (!validationResult?.projectNumber?.isValid) {
        form.setFields([
          {
            name: 'subprojectNumber',
            errors: [validationResult?.projectNumber?.description],
          },
        ]);
      } else {
        form.setFields([
          {
            name: 'subprojectNumber',
            errors: [],
          },
        ]);
      }
    }
  }, [validationResult, form, t, projectToCreateIsValid]);

  useEffect(() => {
    const allValues = form.getFieldsValue();
    if (
      allValues.name &&
      allValues.shortName &&
      allValues.subprojectNumber &&
      allValues.subprojectNumber !== initialValues.subprojectNumber &&
      allValues.subprojectNumber.length > 0 &&
      validationResult?.projectNumber.isValid
    ) {
      setDisableActionButton(false);
    } else {
      setDisableActionButton(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, validationResult, form.getFieldsValue()]);

  //#endregion

  return (
    <Modal
      visible={visible}
      onOk={handleOk}
      onCancel={handleClose}
      okButtonProps={{ disabled: disableActionButton }}
      okText={t('projects:createSubprojectModal.okText')}
      cancelText={t('projects:createSubprojectModal.cancelText')}
      title={t('projects:createSubprojectModal.title')}
      destroyOnClose
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onValuesChange={(_changedValues, allValues) => {
          if (
            allValues.name &&
            allValues.shortName &&
            allValues.subprojectNumber &&
            allValues.subprojectNumber !== initialValues.subprojectNumber &&
            allValues.subprojectNumber.length > 0
          ) {
            setDisableActionButton(false);
          } else {
            setDisableActionButton(true);
          }
        }}
      >
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={24}>
            <Form.Item
              label={t('projects:createSubprojectModal.labels.number')}
              name="subprojectNumber"
              rules={[
                {
                  required: true,
                  message: t('projects:form.validation.missingNumber'),
                },
                () => ({
                  async validator(rule, value) {
                    await validateProjectCreationData();
                  },
                }),
              ]}
            >
              <Input
                placeholder={
                  projectNrPlaceholder ?? defaultProjectNrPlaceholder
                }
                style={{ width: 'calc(50% - 12px)' }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={12}>
            <Form.Item
              label={t('projects:createSubprojectModal.labels.name')}
              name="name"
              rules={[
                {
                  required: true,
                  message: t('projects:form.validation.missingName'),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('projects:createSubprojectModal.labels.shortName')}
              name="shortName"
              rules={[
                {
                  required: true,
                  message: t('projects:form.validation.missingShortName'),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateSubprojectModal;
